import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api, { apiErrorText } from '../utils/api';
import { clearPlanUpgradeContext } from '../utils/planUpgrade';
import i18n from '../i18n/config';

type SupportedLanguage = 'tr' | 'en';

interface Permissions {
  positions?: boolean;
  applications_approve?: boolean;
  applications_delete?: boolean;
  interviews?: boolean;
  interview_flows_create?: boolean;
  interview_flows_edit?: boolean;
  interview_flows_delete?: boolean;
  case_study?: boolean;
  questions?: boolean;
  question_sets?: boolean;
  settings_profile?: boolean;
  settings_hr_interview?: boolean;
  settings_tech_interview?: boolean;
  settings_email?: boolean;
  answer_templates?: boolean;
}

interface User {
  id: number;
  email: string;
  full_name: string;
  role: string;
  tenant_id: number | null;
  is_onboarding_complete?: boolean;
  preferred_language?: SupportedLanguage;
  permissions?: Permissions;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string, totp_code?: string) => Promise<User>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const normalizeLanguage = (lang?: string): SupportedLanguage => (lang === 'en' ? 'en' : 'tr');

  const applyPreferredLanguage = (lang?: string) => {
    const target = normalizeLanguage(lang);
    if (i18n.language !== target) {
      i18n.changeLanguage(target);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const storedTenantSlug = localStorage.getItem('tenantSlug');

      if (storedToken && storedUser) {
        setToken(storedToken);
        const userData = JSON.parse(storedUser);
        const normalizedUser = {
          ...userData,
          preferred_language: normalizeLanguage(userData.preferred_language)
        };
        setUser(normalizedUser);
        applyPreferredLanguage(normalizedUser.preferred_language);
        clearPlanUpgradeContext();

        // If user has tenant but slug is missing, fetch it
        if (userData.tenant_id && !storedTenantSlug) {
          try {
            const tenantResponse = await api.get('/api/tenants/me');
            localStorage.setItem('tenantSlug', tenantResponse.data.slug);
          } catch (error) {
            console.error('Failed to fetch tenant slug:', error);
          }
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string, totp_code?: string) => {
    try {
      const payload: any = { email, password };
      if (totp_code) {
        payload.totp_code = totp_code;
      }
      const response = await api.post('/api/auth/login', payload);
      const { access_token, user: userData } = response.data;

      setToken(access_token);
      const normalizedUser = {
        ...userData,
        preferred_language: normalizeLanguage(userData.preferred_language)
      };
      setUser(normalizedUser);
      applyPreferredLanguage(normalizedUser.preferred_language);

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      clearPlanUpgradeContext();

      // Store tenant slug if user has tenant
      if (userData.tenant_id) {
        // Fetch tenant info to get slug
        const tenantResponse = await api.get('/api/tenants/me');
        localStorage.setItem('tenantSlug', tenantResponse.data.slug);
      }

      return userData;
    } catch (error: any) {
      if (error?.response) {
        const detail = apiErrorText(error.response?.data?.detail);
        // Keep the raw response for callers that need to inspect status / detail object
        // (e.g. 2FA flow, demo expiry). Only set a string message.
        if (typeof detail === 'string') {
          error.message = detail;
        } else if (detail && typeof detail === 'object' && detail.message) {
          error.message = detail.message;
        }
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Notify backend to blacklist the token (best-effort)
      await api.post('/api/auth/logout', {});
    } catch (e) {
      // Ignore errors — logout should still proceed client-side
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantSlug');
    clearPlanUpgradeContext();
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      if (userData.preferred_language) {
        updatedUser.preferred_language = normalizeLanguage(userData.preferred_language);
        applyPreferredLanguage(updatedUser.preferred_language);
      }
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token,
        isLoading,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
